// 브라우저에서 프로필 이미지 리사이즈/압축 유틸
// - 목적: 업로드 용량/해상도 제한으로 로딩/스토리지/전송 비용 절감
// - 주 사용처: 공개 명함/소개페이지 프로필 이미지

function supportsCreateImageBitmap() {
  try {
    return typeof createImageBitmap === 'function'
  } catch {
    return false
  }
}

async function loadBitmap(file) {
  if (supportsCreateImageBitmap()) {
    return await createImageBitmap(file)
  }

  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = reject
      el.src = url
    })
    return img
  } finally {
    try { URL.revokeObjectURL(url) } catch { }
  }
}

function getBitmapSize(bmp) {
  const w = bmp?.width || bmp?.naturalWidth || 0
  const h = bmp?.height || bmp?.naturalHeight || 0
  return { w, h }
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((blob) => resolve(blob), type, quality)
    } catch {
      resolve(null)
    }
  })
}

function guessExtByMime(mime) {
  const m = String(mime || '').toLowerCase()
  if (m === 'image/webp') return 'webp'
  if (m === 'image/png') return 'png'
  if (m === 'image/gif') return 'gif'
  return 'jpg'
}

/**
 * @param {File} file
 * @param {{maxSide?: number, quality?: number, preferWebp?: boolean, maxBytes?: number}} opts
 * @returns {Promise<File>} resized/compressed file
 */
export async function resizeForProfileUpload(file, opts = {}) {
  if (!file) throw new Error('no_file')
  if (!String(file.type || '').toLowerCase().startsWith('image/')) {
    throw new Error('not_image')
  }

  const maxSide = Number.isFinite(opts.maxSide) ? opts.maxSide : 1024
  const quality = Number.isFinite(opts.quality) ? opts.quality : 0.82
  const preferWebp = opts.preferWebp !== false
  const maxBytes = Number.isFinite(opts.maxBytes) ? opts.maxBytes : null

  const bmp = await loadBitmap(file)
  const { w, h } = getBitmapSize(bmp)
  if (!w || !h) return file

  // 이미 충분히 작으면 그대로(불필요한 재인코딩 방지)
  const longSide = Math.max(w, h)
  if (longSide <= maxSide && (!maxBytes || file.size <= maxBytes)) {
    try { bmp?.close?.() } catch { }
    return file
  }

  const qLevels = [
    quality,
    Math.max(0.45, quality * 0.85),
    Math.max(0.40, quality * 0.70),
    Math.max(0.35, quality * 0.55),
  ]

  function drawCanvas(nw, nh) {
    const canvas = document.createElement('canvas')
    canvas.width = nw
    canvas.height = nh
    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return null
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(bmp, 0, 0, nw, nh)
    return canvas
  }

  async function encodeWithin(canvas) {
    // 1) webp 시도 → 2) jpeg (png는 용량 제어가 약해서 최후 수단)
    for (const q of qLevels) {
      if (preferWebp) {
        const b = await canvasToBlob(canvas, 'image/webp', q)
        if (b && b.size > 0 && (!maxBytes || b.size <= maxBytes)) return { blob: b, type: 'image/webp' }
      }
      {
        const b = await canvasToBlob(canvas, 'image/jpeg', q)
        if (b && b.size > 0 && (!maxBytes || b.size <= maxBytes)) return { blob: b, type: 'image/jpeg' }
      }
    }
    // 용량 제한이 없으면 마지막 결과라도 반환
    if (!maxBytes) {
      if (preferWebp) {
        const b = await canvasToBlob(canvas, 'image/webp', quality)
        if (b && b.size > 0) return { blob: b, type: 'image/webp' }
      }
      const b = await canvasToBlob(canvas, 'image/jpeg', quality)
      if (b && b.size > 0) return { blob: b, type: 'image/jpeg' }
      const p = await canvasToBlob(canvas, 'image/png', 0.92)
      if (p && p.size > 0) return { blob: p, type: 'image/png' }
    }
    return null
  }

  try {
    // 1) 주어진 maxSide로 시도, 2) 그래도 크면 해상도를 단계적으로 더 줄여 시도
    let ratio = Math.min(1, maxSide / longSide)
    for (let dimTry = 0; dimTry < 3; dimTry++) {
      const nw = Math.max(1, Math.round(w * ratio))
      const nh = Math.max(1, Math.round(h * ratio))
      const canvas = drawCanvas(nw, nh)
      if (!canvas) return file
      const encoded = await encodeWithin(canvas)
      if (encoded?.blob) {
        const ext = guessExtByMime(encoded.type)
        const baseName = String(file.name || 'image').replace(/\.[^.]+$/, '')
        const nextName = `${baseName}.${ext}`
        return new File([encoded.blob], nextName, { type: encoded.type, lastModified: Date.now() })
      }
      ratio *= 0.85
    }
    throw new Error('too_large_after_resize')
  } finally {
    try { bmp?.close?.() } catch { }
  }
}

